import {
  Badge,
  Button,
  Combobox,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  useCombobox,
} from '@mantine/core'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TbDownload } from 'react-icons/tb'
import type { ControlDataSchema, SyncResponse } from '@/api/main/types.gen'

interface Props {
  opened: boolean
  onClose: () => void
  snapshot: string | null
  schemas: ControlDataSchema[]
  isSubmitting: boolean
  onSync: (
    snapshotName: string,
    destinationSchema: string,
    overridesFrom: string | null
  ) => Promise<SyncResponse[] | undefined>
}

const NEW_SCHEMA_PATTERN = /^[a-z0-9_]+$/

type Step = 'form' | 'confirm' | 'result'

export function SyncSnapshotModal({ opened, onClose, snapshot, schemas, isSubmitting, onSync }: Props) {
  const { t } = useTranslation('mapSnapshots')
  const combobox = useCombobox()
  const [search, setSearch] = useState('')
  const [result, setResult] = useState<SyncResponse[] | null>(null)
  const [step, setStep] = useState<Step>('form')
  const [pendingDestination, setPendingDestination] = useState('')
  const [pendingIsNew, setPendingIsNew] = useState(false)
  const [pendingOverridesFrom, setPendingOverridesFrom] = useState<string | null>(null)
  const [startedAt, setStartedAt] = useState<number | null>(null)
  const [finishedAt, setFinishedAt] = useState<number | null>(null)

  const trimmedSearch = search.trim()
  const matchingSchema = useMemo(() => schemas.find(s => s.schema_name === trimmedSearch), [schemas, trimmedSearch])
  const showCreateOption = trimmedSearch.length > 0 && !matchingSchema && NEW_SCHEMA_PATTERN.test(trimmedSearch)

  const handleClose = () => {
    setSearch('')
    setResult(null)
    setStep('form')
    setPendingDestination('')
    setPendingOverridesFrom(null)
    setStartedAt(null)
    setFinishedAt(null)
    onClose()
  }

  const requestConfirm = (destination: string, isNew: boolean) => {
    if (!snapshot) return
    setPendingDestination(destination)
    setPendingIsNew(isNew)
    setPendingOverridesFrom(isNew ? null : destination)
    setStep('confirm')
  }

  const executeSync = async () => {
    if (!snapshot) return
    const start = Date.now()
    setStartedAt(start)
    setFinishedAt(null)
    const r = await onSync(snapshot, pendingDestination, pendingOverridesFrom)
    if (r) {
      setFinishedAt(Date.now())
      setResult(r)
      setStep('result')
    }
  }

  const handleDownloadJson = () => {
    if (!result || !snapshot) return
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `sync-${snapshot}-${Date.now()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <Modal opened={opened} onClose={handleClose} title={t('syncSnapshot')} size="lg" padding="lg" yOffset="5vh">
      <Stack style={{ minHeight: 280 }}>
        {step === 'form' ? (
          <>
            <Text size="sm">
              {t('syncFromLabel')}{' '}
              <Text component="span" fw={600}>
                {snapshot ?? ''}
              </Text>
            </Text>

            <Combobox
              store={combobox}
              onOptionSubmit={value => {
                if (value === '__create__') {
                  requestConfirm(trimmedSearch, true)
                } else {
                  requestConfirm(value, false)
                }
                combobox.closeDropdown()
              }}
            >
              <Combobox.Target>
                <TextInput
                  label={t('destinationSchema')}
                  description={t('destinationSchemaHelp')}
                  placeholder={t('destinationSchemaPlaceholder')}
                  value={search}
                  onChange={e => {
                    setSearch(e.currentTarget.value)
                    combobox.openDropdown()
                    combobox.updateSelectedOptionIndex()
                  }}
                  onClick={() => combobox.openDropdown()}
                  onFocus={() => combobox.openDropdown()}
                  onBlur={() => combobox.closeDropdown()}
                />
              </Combobox.Target>
              <Combobox.Dropdown mah="40vh" style={{ overflowY: 'auto' }} pb="xs">
                <Combobox.Options>
                  {showCreateOption ? (
                    <Combobox.Option value="__create__">
                      <Group gap="xs">
                        <Badge color="orange" size="sm" variant="light">
                          {t('new')}
                        </Badge>
                        <Text size="sm">{t('createNewSchema', { name: trimmedSearch })}</Text>
                      </Group>
                    </Combobox.Option>
                  ) : null}
                  {schemas
                    .filter(s =>
                      trimmedSearch ? s.schema_name.toLowerCase().includes(trimmedSearch.toLowerCase()) : true
                    )
                    .map(schema => (
                      <Combobox.Option value={schema.schema_name} key={schema.schema_name}>
                        <Group justify="space-between" gap="xs">
                          <Text size="sm" ff="monospace">
                            {schema.schema_name}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {schema.map_version ? `v${schema.map_version}` : null}
                          </Text>
                        </Group>
                      </Combobox.Option>
                    ))}
                  {!showCreateOption && schemas.length === 0 ? <Combobox.Empty>{t('noSchemas')}</Combobox.Empty> : null}
                </Combobox.Options>
              </Combobox.Dropdown>
            </Combobox>

            <Group justify="flex-end">
              <Button variant="default" onClick={handleClose}>
                {t('cancel')}
              </Button>
            </Group>
          </>
        ) : step === 'confirm' ? (
          <Stack>
            <Text fw={600}>{pendingIsNew ? t('confirmCreateSchemaTitle') : t('confirmSyncTitle')}</Text>
            <Text size="sm">{t('confirmSyncSummary', { snapshot, schema: pendingDestination })}</Text>
            <Text size="sm" c={pendingIsNew ? 'orange' : 'dimmed'}>
              {pendingIsNew ? t('confirmCreateSchemaWarning') : t('confirmSyncExistingWarning')}
            </Text>
            <Select
              label={t('overridesFrom')}
              description={t('overridesFromHelp')}
              data={schemas.map(s => ({ value: s.schema_name, label: s.schema_name }))}
              value={pendingOverridesFrom}
              onChange={setPendingOverridesFrom}
              clearable
              searchable
            />
            <Text size="sm" c={pendingOverridesFrom ? 'dimmed' : 'orange'}>
              {pendingOverridesFrom
                ? t('confirmOverridesFrom', { name: pendingOverridesFrom })
                : t('confirmNoOverrides')}
            </Text>
            <Group justify="flex-end" gap="xs" mt="md">
              <Button variant="default" onClick={() => setStep('form')}>
                {t('back')}
              </Button>
              <Button color={pendingIsNew ? 'orange' : 'blue'} loading={isSubmitting} onClick={executeSync}>
                {t('confirm')}
              </Button>
            </Group>
          </Stack>
        ) : (
          <Stack gap="xs">
            <Group justify="space-between" align="center">
              <Text size="sm" fw={600}>
                {t('syncResult')}
              </Text>
              <Button size="xs" variant="light" leftSection={<TbDownload size={14} />} onClick={handleDownloadJson}>
                {t('downloadJson')}
              </Button>
            </Group>
            <Stack gap={2}>
              <Text size="sm">
                {t('syncedFrom')}{' '}
                <Text component="span" fw={600}>
                  {snapshot ?? ''}
                </Text>
              </Text>
              <Text size="sm">
                {t('syncedTo')}{' '}
                <Text component="span" fw={600}>
                  {pendingDestination}
                </Text>
              </Text>
              <Text size="sm">
                {t('overridesCopiedFrom')}{' '}
                <Text component="span" fw={600}>
                  {pendingOverridesFrom ?? t('none')}
                </Text>
              </Text>
              {startedAt != null && (
                <Text size="xs" c="dimmed">
                  {t('syncStarted')} {formatTime(startedAt)}
                  {finishedAt != null && (
                    <>
                      {' · '}
                      {t('syncFinished')} {formatTime(finishedAt)}
                      {' · '}
                      {t('syncDuration')} {Math.round((finishedAt - startedAt) / 1000)}s
                    </>
                  )}
                </Text>
              )}
            </Stack>
            <Table withColumnBorders striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('level')}</Table.Th>
                  <Table.Th>{t('table')}</Table.Th>
                  <Table.Th>{t('message')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {result?.map((row, i) => (
                  <Table.Tr key={i}>
                    <Table.Td>
                      <Badge color={levelColor(row.level)} variant="light">
                        {row.level}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text ff="monospace" size="sm">
                        {row.table}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{row.text}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            <Group justify="flex-end">
              <Button variant="default" onClick={handleClose}>
                {t('close')}
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </Modal>
  )
}

const formatTime = (ms: number) =>
  new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })

const levelColor = (level: string) => {
  switch (level.toLowerCase()) {
    case 'error':
      return 'red'
    case 'warn':
    case 'warning':
      return 'orange'
    case 'info':
      return 'blue'
    default:
      return 'gray'
  }
}
